import { usePersonnel } from 'features/Responder/features/Personnel';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RRule } from 'rrule';
import apiClient from 'services/_infra/apiClient';

const AssignmentsContext = createContext();

export const AssignmentsProvider = ({ children }) => {
  // The active schedule being targeted
  const { activeEditSchedule } = useSchedules();
  // List of personnel
  const { personnel } = usePersonnel();
  // Prevent losing unsaved changes
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  // All assignments fetched from the backend, mapped by user id. This is the source of truth for all assignment data in this UI.
  const [assignments, setAssignments] = useState({});
  // The assignments for the activeEditSchedule, mapped by user id. This is what the UI works with directly.
  const [activeEditAssignments, setActiveEditAssignments] = useState({});
  // Ref to track when we're in the middle of an upload operation
  const isUploadingRef = useRef(false);

  // Retrieve all assignments from the backend and map them by user id for easy access.
  const fetchAllAssignments = useCallback(async () => {
    const res = await apiClient.get(`/responder/schedules/assignments`);
    console.log('Fetched assignments', res, res?.data);
    if (res?.data) {
      buildMappedAssignments(res.data);
    }
  }, []);

  useEffect(() => {
    fetchAllAssignments();
  }, []);

  // Map the list of assignments into an object keyed by personnel_id for easier access in the UI.
  // Takes in an array of assignment objects and produces an object like { [personnel_id]: [assignment, assignment, ...], ... }.
  const buildMappedAssignments = useCallback((assignments) => {
    if (assignments.length > 0) {
      const mappedToUsers = {};
      assignments.forEach((assignment) => {
        // If already exists, push. Else, create new entry
        if (mappedToUsers[assignment.personnel_id]) {
          mappedToUsers[assignment.personnel_id].push(assignment);
        } else {
          mappedToUsers[assignment.personnel_id] = [assignment];
        }
      });
      console.log('Mapped assignments to users', mappedToUsers);
      setAssignments(mappedToUsers);
    }
  }, []);

  // For the currently active schedule, build a mapping of assignments by user that includes only assignments related to that schedule
  // Also includes placeholder "disabled" entries for users who have no assignments for this schedule so the UI can render toggles for them.
  const buildMappedAssignmentsForSchedule = useCallback(
    (assignmentsByUser) => {
      console.log(
        'Building mapped assignments for activeEditSchedule',
        activeEditSchedule,
        assignmentsByUser,
      );
      if (!assignmentsByUser || !activeEditSchedule) return {};
      if (!activeEditSchedule?.id) return {};
      const relatedAssignments = {};
      Object.values(assignmentsByUser).forEach((userAssignments) => {
        userAssignments.forEach((assignment) => {
          // Check if related to active schedule.
          if (assignment.definition_id === activeEditSchedule.id) {
            // Inject enabled value for UI handling.
            const withEnabled = { ...assignment, enabled: true };
            // If already exists, push. Else, create new entry
            if (relatedAssignments[assignment.personnel_id]) {
              relatedAssignments[assignment.personnel_id].push(withEnabled);
            } else {
              relatedAssignments[assignment.personnel_id] = [withEnabled];
            }
          }
        });
      });

      // For every user in the assignments state who has no entry for this schedule,
      // create a disabled placeholder so toggles have an object to work with.
      Object.keys(personnel).forEach((personnelId) => {
        if (!relatedAssignments[personnelId]) {
          relatedAssignments[personnelId] = [
            {
              personnel_id: Number(personnelId),
              definition_id: activeEditSchedule.id,
              recurrence: null,
              notes: null,
              enabled: false,
              start: null,
            },
          ];
        }
      });

      // Decompose any multi-day and/or multi-hour FREQ=WEEKLY rrules into fully atomic
      // single-day/single-hour entries. This mirrors the two merge passes in
      // mergeAssignmentRRules (byweekday then byhour) in reverse, so that:
      //   • toggles are simple exact-match operations against the option rrules, and
      //   • mergeAssignmentRRules recombines them cleanly at upload time.
      // The first split entry for each source entry inherits the backend id so the
      // re-merged record causes uploadAssignments to PUT rather than DELETE + POST.
      const decomposeEntries = (entries) => {
        const result = [];
        for (const a of entries) {
          if (!a.recurrence) {
            result.push(a);
            continue;
          }
          let parsed;
          try {
            parsed = RRule.parseString(a.recurrence);
          } catch {
            result.push(a);
            continue;
          }

          if (parsed.freq !== RRule.WEEKLY) {
            result.push(a);
            continue;
          }

          const days = parsed.byweekday != null ? [].concat(parsed.byweekday) : [null];
          const hours = parsed.byhour != null ? [].concat(parsed.byhour) : [null];

          if (days.length <= 1 && hours.length <= 1) {
            result.push(a);
            continue;
          }

          // Cartesian product of days × hours → one entry each.
          // The first atom keeps the source entry's backend id; subsequent atoms have none.
          let isFirstAtom = true;
          for (const wd of days) {
            for (const hr of hours) {
              try {
                const atomicOpts = { ...parsed };
                if (wd !== null) atomicOpts.byweekday = [wd];
                if (hr !== null) atomicOpts.byhour = [hr];
                const singleRRule = new RRule(atomicOpts).toString();
                const entry = { ...a, recurrence: singleRRule };
                if (!isFirstAtom) delete entry.id;
                result.push(entry);
                isFirstAtom = false;
              } catch {
                if (isFirstAtom) {
                  result.push(a); // fallback: keep original once
                  isFirstAtom = false;
                }
              }
            }
          }
        }
        return result;
      };

      Object.keys(relatedAssignments).forEach((personnelId) => {
        relatedAssignments[personnelId] = decomposeEntries(relatedAssignments[personnelId]);
      });

      console.log('Related assignments for activeEditSchedule', relatedAssignments);
      return relatedAssignments;
    },
    [activeEditSchedule],
  );

  // When the active schedule or assignments change, rebuild the mapped assignments
  useEffect(() => {
    // If still uploading a batch, don't update the local state.
    if (isUploadingRef.current) return;
    if (activeEditSchedule && assignments && Object.keys(assignments).length > 0) {
      setActiveEditAssignments(buildMappedAssignmentsForSchedule(assignments));
    }
  }, [activeEditSchedule, assignments]);

  // Create a new entry for the assignment
  const saveAssignment = useCallback(async (assignment) => {
    console.log('Saving assignment', assignment);
    try {
      const res = await apiClient.post(`/responder/schedules/assignments`, assignment);
      console.log('Saved assignment', res, res?.data);

      // If successful, update the local state. Either push or create new array.
      if (res?.data) {
        const saved = res.data[0];
        setAssignments((prev) => ({
          ...prev,
          [saved.personnel_id]: prev[saved.personnel_id]
            ? [...prev[saved.personnel_id], saved]
            : [saved],
        }));
        setUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Failed to save assignment', err);
    }
  }, []);

  const updateAssignment = useCallback(async (assignment) => {
    try {
      console.log('Updating assignment', assignment);
      // return;

      //if no id, find id from existing assignments for this user and definition
      if (!assignment.id) {
        // Match by both definition_id AND recurrence so we don't confuse
        // a "null = always" assignment with a single-instance one.
        const existing = assignments[assignment.personnel_id]?.find(
          (a) =>
            a.definition_id === assignment.definition_id && a.recurrence === assignment.recurrence,
        );
        if (existing) {
          assignment.id = existing.id;
        } else {
          console.warn(
            'No existing assignment found for user/definition/recurrence, cannot update. Assignment:',
            assignment,
          );
          return;
        }
      }
      const res = await apiClient.put(
        `/responder/schedules/assignments/${assignment.id}`,
        assignment,
      );
      console.log('Updated assignment', res, res?.data);

      // If successful, update the local state. Either push or create new array.
      if (res?.data) {
        const updated = res.data;
        setAssignments((prev) => ({
          ...prev,
          [updated.personnel_id]: prev[updated.personnel_id]
            ? prev[updated.personnel_id].map((a) => (a.id === updated.id ? updated : a))
            : [updated],
        }));
        setUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Failed to update assignment', err);
    }
  }, []);

  const deleteAssignment = useCallback(
    async (assignment) => {
      try {
        console.log('Deleting assignment', assignment);
        // return;
        if (!assignment.id) {
          // Match by definition_id AND recurrence to avoid deleting the wrong entry.
          const existing = assignments[assignment.personnel_id]?.find(
            (a) =>
              a.definition_id === assignment.definition_id &&
              a.recurrence === assignment.recurrence,
          );
          if (!existing?.id) {
            console.warn('No existing assignment id found, cannot delete. Assignment:', assignment);
            return;
          }
          assignment = { ...assignment, id: existing.id };
        }
        await apiClient.delete(`/responder/schedules/assignments/${assignment.id}`);

        // If successful, update the local state. Either push or create new array.
        setAssignments((prev) => {
          const updated = { ...prev };
          updated[assignment.personnel_id] = updated[assignment.personnel_id]?.filter(
            (a) => a.id !== assignment.id,
          );
          if (!updated[assignment.personnel_id]?.length) {
            delete updated[assignment.personnel_id];
          }
          return updated;
        });
      } catch (err) {
        console.error('Failed to delete assignment', err);
      }
    },
    [assignments],
  );

  // Wrapper for accessors to update the activeEditAssignments and mark unsaved changes
  const updateActiveEditAssignments = useCallback((update) => {
    setActiveEditAssignments(update);
    setUnsavedChanges(true);
  }, []);

  // Resets the activeEditAssignments to match the backend state, discarding any unsaved changes
  const clearActiveEditAssignments = useCallback(() => {
    setActiveEditAssignments(buildMappedAssignmentsForSchedule(assignments));
    setUnsavedChanges(false);
  }, []);

  // Batch upload a set of assignments for the active schedule
  const uploadAssignments = useCallback(
    async (assignmentsByUser) => {
      console.log('Sending assignments', assignmentsByUser, activeEditSchedule, assignments);

      // Prevent updating state while uploading
      isUploadingRef.current = true;
      try {
        const allAssignments = Object.values(assignmentsByUser).flat();

        // Pass 1: deletions first so a PUT on the same recurrence slot doesn't collide
        // with a stale backend record that is about to be removed.
        for (const assignment of allAssignments) {
          if (assignment.enabled !== false) continue;
          const existsInBackend =
            !!assignment.id ||
            !!assignments[assignment.personnel_id]?.some(
              (a) =>
                a.definition_id === assignment.definition_id &&
                a.recurrence === assignment.recurrence,
            );
          if (existsInBackend) {
            await deleteAssignment(assignment);
          }
        }

        // Pass 2: updates and creates.
        for (const assignment of allAssignments) {
          if (assignment.enabled === false) continue;
          const existsInBackend =
            !!assignment.id ||
            !!assignments[assignment.personnel_id]?.some(
              (a) =>
                a.definition_id === assignment.definition_id &&
                a.recurrence === assignment.recurrence,
            );
          if (existsInBackend) {
            const backendRecord = assignments[assignment.personnel_id]?.find(
              (a) =>
                (assignment.id ? a.id === assignment.id : true) &&
                a.definition_id === assignment.definition_id &&
                a.recurrence === assignment.recurrence,
            );
            const fieldsToCompare = ['recurrence', 'notes', 'personnel_id', 'definition_id'];
            const isUnchanged =
              !!backendRecord &&
              fieldsToCompare.every((field) => backendRecord[field] === assignment[field]);
            if (isUnchanged) {
              console.log('Assignment unchanged, skipping update', assignment);
            } else {
              await updateAssignment(assignment);
            }
          } else {
            await saveAssignment(assignment);
          }
        }
      } finally {
        isUploadingRef.current = false;
        // Single rebuild after the entire batch is complete
        setAssignments((latest) => {
          setActiveEditAssignments(buildMappedAssignmentsForSchedule(latest));
          return latest;
        });
      }
    },
    [activeEditSchedule, assignments, deleteAssignment],
  );

  const contextValue = {
    assignments,
    setAssignments,
    uploadAssignments,
    activeEditAssignments,
    setActiveEditAssignments: updateActiveEditAssignments,
    unsavedChanges,
    setUnsavedChanges,
    clearActiveEditAssignments,
  };

  return <AssignmentsContext.Provider value={contextValue}>{children}</AssignmentsContext.Provider>;
};

export const useAssignments = () => {
  const context = useContext(AssignmentsContext);
  if (!context) {
    throw new Error('useAssignments must be used within an AssignmentsProvider');
  }
  return context;
};
